import React, { useState, useMemo } from 'react';
import { Card, Row, Col, Table, ProgressBar, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMapMarkerAlt,
    faUsers,
    faUserCheck,
    faPercentage,
    faSearch,
    faSort,
    faSortUp,
    faSortDown,
    faToggleOn,
    faToggleOff,
    faRefresh,
    faClock
} from '@fortawesome/free-solid-svg-icons';
import StatsCard from '../../component/Cards/StatsCard';
import StatsCardWithProgress from '../../component/Cards/StatsCardWithProgress';
import moment from 'moment';

interface CityWiseDataItem {
    total: number;
    present: number;
    city: string;
    percentage: number;
}

interface CityWiseOverviewProps {
    data: CityWiseDataItem[];
    loading: boolean;
    autoRefresh: boolean;
    setAutoRefresh: (value: boolean) => void;
    getData: () => void;
    setNextRefreshIn: (seconds: number) => void;
    lastSynced?: Date | null;
    nextRefreshIn: number;
}

type SortField = 'city' | 'total' | 'present' | 'percentage';
type SortOrder = 'asc' | 'desc';

export default function CityWiseOverview({ data, loading, autoRefresh, setAutoRefresh, getData, setNextRefreshIn, lastSynced, nextRefreshIn }: CityWiseOverviewProps) {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState<SortField>('percentage');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

    // Filter and sort data
    const filteredAndSortedData = useMemo(() => {
        if (!data || data.length === 0) return [];

        // Filter by search term
        let filtered = data.filter(city =>
            city?.city?.toLowerCase().includes(searchTerm?.toLowerCase())
        );

        // Sort data
        filtered.sort((a, b) => {
            let aValue: number | string;
            let bValue: number | string;

            switch (sortField) {
                case 'city':
                    aValue = a.city?.toLowerCase();
                    bValue = b.city?.toLowerCase();
                    break;
                case 'total':
                    aValue = a.total;
                    bValue = b.total;
                    break;
                case 'present':
                    aValue = a.present;
                    bValue = b.present;
                    break;
                case 'percentage':
                    aValue = a.percentage;
                    bValue = b.percentage;
                    break;
                default:
                    return 0;
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortOrder === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            } else {
                return sortOrder === 'asc'
                    ? (aValue as number) - (bValue as number)
                    : (bValue as number) - (aValue as number);
            }
        });

        return filtered;
    }, [data, searchTerm, sortField, sortOrder]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('desc');
        }
    };

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) {
            return faSort;
        }
        return sortOrder === 'asc' ? faSortUp : faSortDown;
    };
    if (!data || data.length === 0) {
        return (
            <Card className="mt-4 shadow" style={{ borderRadius: "16px", border: "1px solid #e9ecef" }}>
                <Card.Body className="p-4">
                    <h5 className="text-secondary fw-bold mb-4" style={{ fontSize: '18px' }}>
                        City-wise Attendance
                    </h5>
                    <div className="text-center py-5">
                        <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            className="text-muted"
                            style={{ fontSize: '48px', opacity: 0.3 }}
                        />
                        <div className="text-muted mt-3" style={{ fontSize: '16px' }}>
                            No city-wise data available
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    // Calculate overall statistics (based on filtered data for accuracy)
    const totalCandidates = filteredAndSortedData.reduce((sum, city) => sum + city.total, 0);
    const totalPresent = filteredAndSortedData.reduce((sum, city) => sum + city.present, 0);
    const overallPercentage = totalCandidates > 0 ? ((totalPresent / totalCandidates) * 100) : 0;

    return (
        <Card className="mt-4 shadow" style={{ borderRadius: "16px", border: "1px solid #e9ecef" }}>
            <Card.Body className="p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="text-secondary fw-bold mb-0" style={{ fontSize: '18px' }}>                    City-wise Attendance Overview
                    </h5>
                    <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center">
                            <FontAwesomeIcon
                                icon={autoRefresh ? faToggleOn : faToggleOff}
                                className={`cursor-pointer me-2 ${autoRefresh ? 'text-success' : 'text-muted'}`}
                                style={{ fontSize: '18px' }}
                                onClick={() => {
                                    setAutoRefresh(!autoRefresh);
                                    if (!autoRefresh) {
                                        setNextRefreshIn(120);
                                    }
                                }}
                                title={`Auto-refresh ${autoRefresh ? 'enabled' : 'disabled'}`}
                            />
                            <small className="text-muted">Auto-refresh</small>
                        </div>

                        <FontAwesomeIcon
                            icon={faRefresh}
                            className={`cursor-pointer text-primary ${loading ? 'fa-spin' : ''}`}
                            onClick={() => {
                                getData();
                                if (autoRefresh) {
                                    setNextRefreshIn(120);
                                }
                            }}
                            style={{ fontSize: '16px' }}
                            title="Refresh now"
                        />
                    </div>
                </div>

                {/* Summary Stats */}
                <Row className="g-3 mb-4">
                    <Col lg={3} md={6}>
                        <StatsCard
                            title="Total Cities"
                            value={filteredAndSortedData.length}
                            icon={faMapMarkerAlt}
                            iconColor="primary"
                            valueColor="primary"
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatsCard
                            title="Total Candidates"
                            value={totalCandidates}
                            icon={faUsers}
                            iconColor="info"
                            valueColor="info"
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatsCard
                            title="Present Candidates"
                            value={totalPresent}
                            icon={faUserCheck}
                            iconColor="success"
                            valueColor="success"
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatsCardWithProgress
                            title="Overall Attendance"
                            value={`${overallPercentage.toFixed(1)}%`}
                            icon={faPercentage}
                            iconColor="warning"
                            valueColor="warning"
                            progress={overallPercentage}
                            progressVariant="warning"
                            progressHeight={8}
                        />
                    </Col>
                </Row>

                {/* Search and Controls */}
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group>
                            <InputGroup>
                                <InputGroup.Text>
                                    <FontAwesomeIcon icon={faSearch} className="text-muted" />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search cities..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ fontSize: '14px' }}
                                />
                            </InputGroup>
                        </Form.Group>
                    </Col>
                    <Col md={6} className="d-flex align-items-end justify-content-end">
                        <small className="text-muted">
                            Showing {filteredAndSortedData.length} of {data.length} cities
                        </small>
                    </Col>
                </Row>

                {/* City-wise Table */}
                <div className="table-responsive">
                    <Table hover className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th
                                    style={{ fontSize: '14px', fontWeight: '600', cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('city')}
                                    className="position-relative"
                                >
                                    <div className="d-flex align-items-center justify-content-between">
                                        City
                                        <FontAwesomeIcon
                                            icon={getSortIcon('city')}
                                            className={`ms-2 ${sortField === 'city' ? 'text-primary' : 'text-muted'}`}
                                            style={{ fontSize: '12px' }}
                                        />
                                    </div>
                                </th>
                                <th
                                    style={{ fontSize: '14px', fontWeight: '600', cursor: 'pointer', userSelect: 'none' }}
                                    className="text-center position-relative"
                                    onClick={() => handleSort('total')}
                                >
                                    <div className="d-flex align-items-center justify-content-center">
                                        Total
                                        <FontAwesomeIcon
                                            icon={getSortIcon('total')}
                                            className={`ms-2 ${sortField === 'total' ? 'text-primary' : 'text-muted'}`}
                                            style={{ fontSize: '12px' }}
                                        />
                                    </div>
                                </th>
                                <th
                                    style={{ fontSize: '14px', fontWeight: '600', cursor: 'pointer', userSelect: 'none' }}
                                    className="text-center position-relative"
                                    onClick={() => handleSort('present')}
                                >
                                    <div className="d-flex align-items-center justify-content-center">
                                        Present
                                        <FontAwesomeIcon
                                            icon={getSortIcon('present')}
                                            className={`ms-2 ${sortField === 'present' ? 'text-primary' : 'text-muted'}`}
                                            style={{ fontSize: '12px' }}
                                        />
                                    </div>
                                </th>
                                <th style={{ fontSize: '14px', fontWeight: '600' }} className="text-center">Absent</th>
                                <th
                                    style={{ fontSize: '14px', fontWeight: '600', cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('percentage')}
                                    className="position-relative"
                                >
                                    <div className="d-flex align-items-center justify-content-between">
                                        Attendance Rate
                                        <FontAwesomeIcon
                                            icon={getSortIcon('percentage')}
                                            className={`ms-2 ${sortField === 'percentage' ? 'text-primary' : 'text-muted'}`}
                                            style={{ fontSize: '12px' }}
                                        />
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAndSortedData.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center py-4 text-muted">
                                        {searchTerm ? `No cities found matching "${searchTerm}"` : 'No data available'}
                                    </td>
                                </tr>
                            ) : (
                                filteredAndSortedData.map((city, index) => {
                                    const absent = city.total - city.present;
                                    const progressVariant = city.percentage >= 80 ? 'success' :
                                        city.percentage >= 60 ? 'warning' : 'danger';

                                    return (
                                        <tr key={city.city}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <FontAwesomeIcon
                                                        icon={faMapMarkerAlt}
                                                        className="text-muted me-2"
                                                        style={{ fontSize: '12px' }}
                                                    />
                                                    <span className="fw-medium">{city.city}</span>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <span className="badge bg-light text-dark">
                                                    {city.total.toLocaleString()}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <span className="badge bg-success">
                                                    {city.present.toLocaleString()}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <span className="badge bg-danger">
                                                    {absent.toLocaleString()}
                                                </span>
                                            </td>
                                            <td style={{ width: '300px' }}>
                                                <div className="d-flex align-items-center">
                                                    <div className="flex-grow-1 me-3">
                                                        <ProgressBar
                                                            now={city.percentage}
                                                            variant={progressVariant}
                                                            style={{ height: '8px' }}
                                                        />
                                                    </div>
                                                    <span
                                                        className={`fw-bold text-${progressVariant}`}
                                                        style={{ fontSize: '14px', minWidth: '45px' }}
                                                    >
                                                        {city.percentage.toFixed(1)}%
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </Table>
                </div>

                {/* Performance Summary */}
                <div className="mt-4 pt-3 border-top">
                    <Row>
                        <Col md={4}>
                            <div className="text-center">
                                <div className="text-success fw-bold" style={{ fontSize: '18px' }}>
                                    {filteredAndSortedData.filter(city => city.percentage >= 80).length}
                                </div>
                                <div className="text-muted" style={{ fontSize: '12px' }}>
                                    High (≥80%)
                                </div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="text-center">
                                <div className="text-warning fw-bold" style={{ fontSize: '18px' }}>
                                    {filteredAndSortedData.filter(city => city.percentage >= 60 && city.percentage < 80).length}
                                </div>
                                <div className="text-muted" style={{ fontSize: '12px' }}>
                                    Average (60-79%)
                                </div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="text-center">
                                <div className="text-danger fw-bold" style={{ fontSize: '18px' }}>
                                    {filteredAndSortedData.filter(city => city.percentage < 60).length}
                                </div>
                                <div className="text-muted" style={{ fontSize: '12px' }}>
                                    Low (&lt;60%)
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>

                {lastSynced && (
                    <div className="mt-3 d-flex align-items-center justify-content-end">
                        <FontAwesomeIcon icon={faClock} className="text-muted me-2" style={{ fontSize: '12px' }} />
                        <small className="text-muted">
                            Last synced: {moment(lastSynced).format('DD MMM YYYY, hh:mm:ss')}
                            {autoRefresh && (
                                <span className="ms-2">
                                    • Next refresh in: <span className="fw-bold text-primary">{formatCountdown(nextRefreshIn)}</span>
                                </span>
                            )}
                        </small>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}

export type { CityWiseDataItem };


const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};